import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ActionButton, ActionTextarea, CardFrame, OptionButton, UndoStrip, useAccess } from '../../parts/index.js';
import { currentViewing, useViewing, viewingRevision } from '../../view-as.js';
import { accessApi } from './api.js';
import { errorWords, joinNames } from './model.js';
import type { Applied, Change, Preview } from './types.js';

export interface PendingChange { anchor: string; change: Change; preview: Preview | null; reason: string; error: string | null; reasonOnly: boolean }
export function useChangeFlow(store: string, committed: () => Promise<void>, savedWords: (change: Change, result: Applied) => string) {
  const [pending, setPending] = useState<PendingChange | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState<{ words: string; undo?: Change | undefined } | null>(null);
  const [undoError, setUndoError] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const view = useViewing();
  const access = useAccess();
  const blocked = !!view || !!access?.viewing;
  const live = useRef(true);
  const epoch = useRef(viewingRevision());
  const controller = useRef<AbortController | null>(null);
  const serial = useRef(0);
  const writing = useRef(false);
  const active = () => live.current && epoch.current === viewingRevision();
  useEffect(() => {
    live.current = true;
    return () => { live.current = false; controller.current?.abort(); ++serial.current; };
  }, []);
  const cancel = () => {
    if (writing.current) return;
    ++serial.current; controller.current?.abort(); setPending(null); setBusy(false);
  };
  const begin = async (anchor: string, change: Change, reason = '', reasonOnly = false) => {
    if (blocked || currentViewing() || writing.current || !active()) return;
    controller.current?.abort(); controller.current = new AbortController();
    const id = ++serial.current;
    setPending({ anchor, change, preview: null, reason, error: null, reasonOnly }); setBusy(true);
    try {
      const preview = await accessApi.preview(store, change, controller.current.signal);
      if (active() && serial.current === id) setPending(p => p ? { ...p, preview } : null);
    } catch (e) {
      if (active() && serial.current === id) setPending(p => p ? { ...p, error: errorWords(e, 'Preview could not be loaded.') } : null);
    } finally { if (active() && serial.current === id) setBusy(false); }
  };
  const save = async () => {
    if (!pending?.preview || !pending.reason.trim() || blocked || currentViewing() || writing.current || !active()) return;
    writing.current = true; setBusy(true); setPending({ ...pending, error: null });
    let applied = false;
    try {
      const result = await accessApi.apply(store, pending.change, pending.reason.trim());
      applied = true;
      if (!active()) return;
      setPending(null); setUndoError(null); setPaused(false);
      setSaved({ words: `Saved: ${savedWords(pending.change, result).replace(/[.!?]$/, '')}.`, undo: result.undo });
      await committed();
    } catch (e) {
      if (active() && !applied) setPending({ ...pending, error: errorWords(e, 'The change could not be saved.') });
    } finally { writing.current = false; if (active()) setBusy(false); }
  };
  const undo = async () => {
    if (!saved?.undo || blocked || currentViewing() || writing.current || !active()) return;
    writing.current = true; setBusy(true); setUndoError(null);
    try {
      await accessApi.apply(store, saved.undo, 'Undone');
      if (!active()) return;
      setSaved(null); ++serial.current; controller.current?.abort(); setPending(null); await committed();
    } catch (e) { if (active()) { setPaused(true); setUndoError(errorWords(e, 'The change could not be undone.')); } }
    finally { writing.current = false; if (active()) setBusy(false); }
  };
  // U34's eight-second window pauses for pointer/keyboard reachability.
  const remaining = useRef(8000);
  useEffect(() => { remaining.current = 8000; }, [saved]);
  useEffect(() => {
    if (!saved || paused || busy) return;
    const started = Date.now();
    const timer = window.setTimeout(() => setSaved(null), remaining.current);
    return () => { window.clearTimeout(timer); remaining.current -= Date.now() - started; };
  }, [saved, paused, busy]);
  useEffect(() => {
    const key = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && pending) { e.preventDefault(); cancel(); return; }
      if (!saved?.undo) return;
      if (e.target instanceof HTMLElement && (e.target.matches('input,textarea,select') || e.target.isContentEditable)) return;
      if (e.key.toLowerCase() === 'z') { e.preventDefault(); void undo(); }
    };
    window.addEventListener('keydown', key); return () => window.removeEventListener('keydown', key);
  });
  const notice = <>
    <UndoStrip words={saved?.words} onUndo={saved?.undo && !busy ? () => void undo() : undefined}
      onMouseEnter={() => setPaused(true)} onMouseLeave={() => { if (!undoError) setPaused(false); }}
      onFocus={() => setPaused(true)} onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget) && !undoError) setPaused(false); }} />
    {undoError ? <div className="access-warning" role="alert">{undoError}<ActionButton className="access-link" disabled={busy} onClick={() => void undo()}>Try again</ActionButton></div> : null}
  </>;
  return { pending, busy, blocked, begin, cancel, save, notice,
    reason: (reason: string) => setPending(p => p ? { ...p, reason } : p),
  };
}
export type ChangeFlow = ReturnType<typeof useChangeFlow>;
/** Brief 5.4.2: "[n] people gain it: ... and [n] more"; a removal loses it. Five names, then a count. */
export function reachWords(change: Change, names: string[]): string {
  const gains = 'enabled' in change ? change.enabled : 'access' in change ? change.access !== null && change.access !== 'none' : true;
  const verb = names.length === 1 ? (gains ? 'gains' : 'loses') : (gains ? 'gain' : 'lose');
  if (!names.length) return 'Nobody is in it yet, so it reaches nobody today.';
  const shown = names.slice(0, 5), more = names.length - shown.length;
  return `${names.length} ${names.length === 1 ? 'person' : 'people'} ${verb} it: ${more ? `${shown.join(', ')} and ${more} more` : joinNames(shown)}.`;
}
export function ConfirmChange({ flow, title, lead = 'Save the change', scope, alternative, peopleWords }: {
  flow: ChangeFlow; title: string; peopleWords?: string | undefined; lead?: string | undefined; scope?: ReactNode; alternative?: (() => void) | undefined;
}) {
  const p = flow.pending;
  if (!p) return null;
  return <CardFrame variant="panel" className="access-confirm" role="region" aria-label="Confirm change" aria-busy={flow.busy}>
    <h3>{title}</h3>
    {scope}
    {p.preview ? <p>{peopleWords ?? reachWords(p.change, p.preview.affected.map(person => person.displayName))}</p> : <p>{p.error ? 'Preview could not be loaded.' : 'Reading who this reaches…'}</p>}
    {p.preview?.history ? <dl className="access-facts">
      <div><dt>Indexed messages</dt><dd>{p.preview.history.olderMessages.why}</dd></div>
      <div><dt>Cost</dt><dd>{p.preview.history.costUsd.why}</dd></div>
      <div><dt>Days</dt><dd>{p.preview.history.days.why}</dd></div>
    </dl> : null}
    <label className="access-field">Reason<ActionTextarea required maxLength={500} value={p.reason} disabled={flow.busy}
      onChange={e => flow.reason(e.target.value)} /></label>
    {p.error ? <p className="access-warning" role="alert">{p.error}</p> : null}
    <div className="access-actions">
      <OptionButton variant="lead" disabled={flow.busy || (!!p.preview && !p.reason.trim())}
        onClick={() => p.preview ? void flow.save() : void flow.begin(p.anchor, p.change, p.reason, p.reasonOnly)}>{p.error ? 'Try again' : lead}</OptionButton>
      {alternative ? <OptionButton disabled={flow.busy} onClick={alternative}>Give it to one person instead</OptionButton> : null}
      <ActionButton className="access-link" disabled={flow.busy && !!p.preview} onClick={flow.cancel}>Cancel</ActionButton>
    </div>
  </CardFrame>;
}
